// src/components/Presentation/PresentationEditor.js (CLEAN ARCHITECTURE)
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Play, Eye, Share2, MoreVertical } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getPresentation, 
  updatePresentation, 
  createLiveSession 
} from '../../services/presentationAPI';
import { documentService } from '../../services/documentService';
import SlideCanvas from './SlideCanvas';
import ContentToolbar from './ContentToolbar';
import SlideNavigation from './SlideNavigation';
import SpeakerNotes from './SpeakerNotes';
import toast from 'react-hot-toast';

const PresentationEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  
  // Core state
  const [presentation, setPresentation] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // UI state
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [showSpeakerNotes, setShowSpeakerNotes] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Load presentation on mount
  useEffect(() => {
    loadPresentation();
  }, [id, user]);

  // Auto-save functionality
  useEffect(() => {
    if (presentation && !isLoading) {
      const autoSaveTimer = setTimeout(() => {
        handleSave(true);
      }, 5000);
      return () => clearTimeout(autoSaveTimer);
    }
  }, [presentation, isLoading]);

  const loadPresentation = async () => {
    try {
      setIsLoading(true);
      const data = await getPresentation(id);
      
      if (data.authorId !== user?.uid) {
        toast.error('Access denied');
        navigate('/presentations');
        return;
      }

      setPresentation(data);
      
      // Ensure at least one slide exists
      if (!data.slides || data.slides.length === 0) {
        const defaultSlide = createDefaultSlide();
        setPresentation(prev => ({ ...prev, slides: [defaultSlide] }));
      }
    } catch (error) {
      console.error('Error loading presentation:', error);
      toast.error('Failed to load presentation');
      navigate('/presentations');
    } finally {
      setIsLoading(false);
    }
  };

  const createDefaultSlide = () => ({
    id: `slide-${Date.now()}`,
    title: 'Slide 1',
    layout: 'single',
    slates: [{
      id: `slate-${Date.now()}`,
      type: 'empty',
      gridArea: { x: 0, y: 0, w: 12, h: 12 },
      content: null
    }],
    background: { type: 'color', value: '#ffffff' },
    notes: ''
  });

  const handleSave = async (silent = false) => {
    if (!presentation || isSaving) return;

    try {
      if (!silent) setIsSaving(true);
      
      await updatePresentation(id, {
        title: presentation.title,
        description: presentation.description || '',
        slides: presentation.slides,
        settings: presentation.settings || {}
      });
      
      if (!silent) toast.success('Presentation saved');
    } catch (error) {
      console.error('Save error:', error);
      if (!silent) toast.error('Failed to save');
    } finally {
      if (!silent) setIsSaving(false);
    }
  };

  // Slide management
  const updateSlide = useCallback((slideIndex, updatedSlide) => {
    setPresentation(prev => ({
      ...prev,
      slides: prev.slides.map((slide, index) => 
        index === slideIndex ? updatedSlide : slide
      )
    }));
  }, []);

  const addSlide = useCallback(() => {
    const newSlide = {
      ...createDefaultSlide(),
      id: `slide-${Date.now()}`,
      title: `Slide ${presentation.slides.length + 1}`
    };
    
    setPresentation(prev => ({
      ...prev,
      slides: [...prev.slides, newSlide]
    }));
    setCurrentSlideIndex(presentation.slides.length);
  }, [presentation?.slides?.length]);

  const deleteSlide = useCallback((slideIndex) => {
    if (presentation.slides.length <= 1) {
      toast.error('Cannot delete the last slide');
      return;
    }
    
    const updatedSlides = presentation.slides.filter((_, index) => index !== slideIndex);
    setPresentation(prev => ({ ...prev, slides: updatedSlides }));
    
    if (currentSlideIndex >= updatedSlides.length) {
      setCurrentSlideIndex(updatedSlides.length - 1);
    }
  }, [presentation?.slides, currentSlideIndex]);

  // Content management - SINGLE SOURCE OF TRUTH
  const addContentToSlate = useCallback(async (slateId, contentType) => {
    const currentSlide = presentation.slides[currentSlideIndex];
    if (!currentSlide || !slateId || !contentType) return;

    // Handle image upload
    if (contentType === 'image') {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.onchange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
          setUploadingImage(true);
          const uploadedFile = await documentService.uploadFile(file, user.uid, 'presentation-images');
          
          const updatedSlate = {
            ...currentSlide.slates.find(s => s.id === slateId),
            type: 'image',
            content: {
              src: uploadedFile.url,
              alt: file.name,
              caption: '',
              objectFit: 'cover'
            }
          };

          const updatedSlides = currentSlide.slates.map(slate => 
            slate.id === slateId ? updatedSlate : slate
          );

          updateSlide(currentSlideIndex, { ...currentSlide, slates: updatedSlides });
          setSelectedElement(slateId);
          toast.success('Image uploaded successfully');
        } catch (error) {
          console.error('Image upload error:', error);
          toast.error('Failed to upload image');
        } finally {
          setUploadingImage(false);
        }
      };
      fileInput.click();
      return;
    }

    // Handle other content types
    const getDefaultContent = (type) => {
      switch (type) {
        case 'text':
          return { 
            content: 'Click to edit text', 
            fontSize: 'text-lg', 
            alignment: 'left', 
            color: '#000000',
            fontWeight: 'font-normal',
            fontStyle: 'normal'
          };
        case 'video':
          return { src: '', title: 'Video Title', autoplay: false };
        case 'poll':
          return {
            question: 'Your poll question?',
            options: ['Option A', 'Option B', 'Option C'],
            allowMultiple: false,
            showResults: true
          };
        case 'question':
          return { prompt: 'Ask your audience...', allowAnonymous: true };
        case 'chart':
          return {
            type: 'bar',
            data: [{ label: 'A', value: 10 }, { label: 'B', value: 20 }],
            title: 'Chart Title'
          };
        default:
          return {};
      }
    };

    const updatedSlate = {
      ...currentSlide.slates.find(s => s.id === slateId),
      type: contentType,
      content: getDefaultContent(contentType)
    };

    const updatedSlides = currentSlide.slates.map(slate => 
      slate.id === slateId ? updatedSlate : slate
    );

    updateSlide(currentSlideIndex, { ...currentSlide, slates: updatedSlides });
    setSelectedContentType(null);
    setSelectedElement(slateId);
    toast.success(`${contentType} added successfully`);
  }, [presentation, currentSlideIndex, updateSlide, user.uid]);

  // Apply layout template
  const applyLayoutTemplate = useCallback((templateId) => {
    const currentSlide = presentation.slides[currentSlideIndex];
    if (!currentSlide) return;

    const templates = {
      single: [{ x: 0, y: 0, w: 12, h: 12 }],
      'two-horizontal': [{ x: 0, y: 0, w: 6, h: 12 }, { x: 6, y: 0, w: 6, h: 12 }],
      'two-vertical': [{ x: 0, y: 0, w: 12, h: 6 }, { x: 0, y: 6, w: 12, h: 6 }],
      'three-column': [{ x: 0, y: 0, w: 4, h: 12 }, { x: 4, y: 0, w: 4, h: 12 }, { x: 8, y: 0, w: 4, h: 12 }]
    };

    const template = templates[templateId];
    if (!template) return;

    const newSlates = template.map((area, index) => ({
      id: `slate-${Date.now()}-${index}`,
      type: 'empty',
      gridArea: area,
      content: null
    }));

    updateSlide(currentSlideIndex, {
      ...currentSlide,
      layout: templateId,
      slates: newSlates
    });
    
    toast.success('Layout applied');
  }, [presentation, currentSlideIndex, updateSlide]);

  // Navigation functions
  const goToPreview = () => navigate(`/presentation/${id}`);
  
  const startLivePresentation = async () => {
    try {
      const session = await createLiveSession(id, user.uid);
      navigate(`/presentation/${id}/present?session=${session.sessionCode}`);
      toast.success('Live presentation started!');
    } catch (error) {
      console.error('Error starting live presentation:', error);
      toast.error('Failed to start live presentation');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading presentation editor...</p>
        </div>
      </div>
    );
  }

  if (!presentation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Presentation not found</p>
          <button
            onClick={() => navigate('/presentations')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Presentations
          </button>
        </div>
      </div>
    );
  }

  const currentSlide = presentation.slides[currentSlideIndex];

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/presentations')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <input
                type="text"
                value={presentation.title}
                onChange={(e) => setPresentation(prev => ({ ...prev, title: e.target.value }))}
                className="text-xl font-semibold text-gray-900 bg-transparent border-none outline-none focus:ring-2 focus:ring-indigo-500 rounded px-2 py-1"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowSpeakerNotes(!showSpeakerNotes)}
              className={`px-3 py-2 rounded-lg border transition-colors ${
                showSpeakerNotes 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-900' 
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Notes
            </button>
            
            <button
              onClick={() => handleSave(false)}
              disabled={isSaving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            
            <button
              onClick={goToPreview}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Eye className="w-4 h-4 mr-2 inline" />
              Preview
            </button>
            
            <button
              onClick={startLivePresentation}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Play className="w-4 h-4 mr-2 inline" />
              Present
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Slide Navigation */}
        <SlideNavigation
          slides={presentation.slides}
          currentSlideIndex={currentSlideIndex}
          onSlideSelect={setCurrentSlideIndex}
          onAddSlide={addSlide}
          onDeleteSlide={deleteSlide}
          onApplyLayout={applyLayoutTemplate}
        />

        {/* Main Editor Area */}
        <div className="flex-1 flex flex-col min-h-[80px] flex-shrink-0 overflow-hidden">
          {/* Content Toolbar */}
          <ContentToolbar
            selectedContentType={selectedContentType}
            onContentTypeSelect={setSelectedContentType}
            selectedElement={selectedElement}
            currentSlide={currentSlide}
            onUpdateSlide={(updatedSlide) => updateSlide(currentSlideIndex, updatedSlide)}
          />

          {/* Slide Canvas */}
          <div className="flex-1 bg-gray-100 flex items-center justify-center p-4 pt-24 overflow-auto">
            {currentSlide ? (
              <SlideCanvas
                slide={currentSlide}
                selectedElement={selectedElement}
                onSelectElement={setSelectedElement}
                selectedContentType={selectedContentType}
                onAddContent={addContentToSlate}
                uploadingImage={uploadingImage}
              />
            ) : (
              <div className="text-center">
                <p className="text-gray-600 mb-4">No slides yet</p>
                <button
                  onClick={addSlide}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Create First Slide
                </button>
              </div>
            )}
          </div>

          {/* Speaker Notes */}
          {showSpeakerNotes && (
            <SpeakerNotes
              notes={currentSlide?.notes || ''}
              onUpdate={(notes) => updateSlide(currentSlideIndex, { ...currentSlide, notes })}
              slideNumber={currentSlideIndex + 1}
              onClose={() => setShowSpeakerNotes(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PresentationEditor;